/**
 * Single-module area chart for simulation performance trends.
 *
 * @module ModuleProgressChart
 */

import type { LucideIcon } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import type { ChartDataPoint } from "@/lib/simulationResultsChartData";

export interface ModuleProgressChartProps {
  readonly title: string;
  readonly description: string;
  readonly icon: LucideIcon;
  readonly iconBgColor: string;
  readonly iconColor: string;
  readonly data: ChartDataPoint[];
  readonly dataKey: string;
  readonly gradientId: string;
  readonly color: string;
  readonly averageScore: number | undefined;
  /** When false, shows not-attempted empty state instead of a flat zero line (B7). */
  readonly hasAttempted?: boolean;
  /** AC4: open detailed review for this module's latest attempt. */
  readonly onOpenReview?: () => void;
  /** Chart plot height class (default h-[200px]) */
  readonly chartHeightClassName?: string;
}

export function ModuleProgressChart({
  title,
  description,
  icon: Icon,
  iconBgColor,
  iconColor,
  data,
  dataKey,
  gradientId,
  color,
  averageScore,
  hasAttempted = true,
  onOpenReview,
  chartHeightClassName = "h-[200px]",
}: ModuleProgressChartProps) {
  const { t } = useTranslation();
  const hasChartPoints = data.some(
    (point) => typeof point[dataKey as keyof ChartDataPoint] === "number",
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3 p-4 sm:p-6">
        <div className={`p-2 rounded-lg ${iconBgColor}`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
        {!hasAttempted || !hasChartPoints ? (
          <div
            className={`flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 text-center ${chartHeightClassName}`}
          >
            <p className="font-ui text-sm text-muted-foreground">
              {t("dashboard.insights.notAttempted", {
                defaultValue: "Not attempted",
              })}
            </p>
          </div>
        ) : (
          <div className={chartHeightClassName}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border"
                />
                <XAxis dataKey="date" className="text-xs" />
                <YAxis domain={[0, 100]} className="text-xs" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey={dataKey}
                  stroke={color}
                  fill={`url(#${gradientId})`}
                  strokeWidth={2}
                  connectNulls
                  dot={{ fill: color, r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        {hasAttempted && averageScore !== undefined && (
          <div className="mt-4 flex items-center justify-between">
            <span className="font-ui text-sm text-muted-foreground">
              {t("simulation.results.stats.averageScore", {
                defaultValue: "Average Score",
              })}
            </span>
            <span className="font-ui text-lg font-semibold">
              {averageScore}%
            </span>
          </div>
        )}
        {hasAttempted && onOpenReview ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="font-ui mt-3 w-full"
            onClick={onOpenReview}
          >
            {t("simulation.results.performanceTrends.viewLatestReview", {
              defaultValue: "View latest review",
            })}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
